/**
 * A post-auth destination that survives the EMAIL round-trip.
 *
 * Router state (`location.state.from`) carries "return here after sign-in"
 * perfectly well within the SPA — but a new signup verifies by email, and a
 * magic link signs in by email, and both come back through /auth/callback in
 * what may be a brand-new tab. Router state is gone by then, so the learner
 * who was promised "we'll bring you straight back to /redeem" landed on the
 * default home with no voucher prompt (Dylan's Jam, Sept 2026).
 *
 * localStorage is the only carrier that survives that trip. One-shot
 * (consume clears), time-boxed to 24h so a stale entry can't hijack an
 * unrelated login next week, and every access is wrapped — storage can be
 * unavailable (private windows, blocked site data) and auth must never
 * break over a convenience redirect.
 */

const KEY = 'kula_post_auth_redirect';
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

/** Persists where to return the user after an auth flow that round-trips
 *  through email. Overwrites any previous entry. */
export function rememberPostAuthRedirect(path: string): void {
  try {
    localStorage.setItem(KEY, JSON.stringify({ path, at: Date.now() }));
  } catch {
    /* storage unavailable — the in-SPA router state still covers same-tab flows */
  }
}

/** Returns the remembered path (and clears it), or null if none/expired.
 *  Only same-origin relative paths are honoured — never a full URL. */
export function consumePostAuthRedirect(): string | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    localStorage.removeItem(KEY);
    const { path, at } = JSON.parse(raw) as { path?: unknown; at?: unknown };
    if (typeof path !== 'string' || typeof at !== 'number') return null;
    if (Date.now() - at > MAX_AGE_MS) return null;
    if (!path.startsWith('/') || path.startsWith('//')) return null;
    return path;
  } catch {
    return null;
  }
}
